"use client";

import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVirtualizer, useWindowVirtualizer, type VirtualItem } from "@tanstack/react-virtual";
import ItemCard, { AD_ACTION_SIGN_IN } from "@/components/ItemCard";
import SelectionCard from "@/components/SelectionCard";
import GhostCard from "../GhostCard";
import styles from "./styles.module.scss";
import {
  Box as MantineBox,
  Loader as MantineLoader,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";
import { useItemsStore } from "@/stores/itemsStore";

type Props = {
  items: SearchResultItem[];
  loading: boolean;
  ghosts?: number;
  noResults?: boolean;
  cart?: boolean;
  handleCardClick?: (id: number) => void;
  showProAd?: boolean;
  /** Which promo copy to inject with showProAd — "tokens" (default, out-of-budget upsell) or "signup" (signed-out visitors). */
  proAdVariant?: "tokens" | "signup";
  priceHasCurrency?: boolean;
  disableFavorites?: boolean;
  enableImageSearch?: boolean;
  /** The scrollable element that contains this grid (for desktop scroll container mode). */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  /** Called when user scrolls near the bottom — triggers loading more items */
  onLoadMore?: () => boolean | void | Promise<boolean | void>;
  /** Called with the deepest visible item after user scroll, for saved-filter resume bookmarks. */
  onResumePositionChange?: (item: SearchResultItem) => void;
  /** Selector for the actual scroll container used by useLazyLoadImage (default: "#masonary") */
  lazyLoadContainerSelector?: string;
  /** When set, the grid will scroll to keep this item in view after layout reflows */
  scrollToItemId?: number | null;
  /** Whether the slide-in is currently open — used to distinguish open vs close scrolling */
  isSlideInOpen?: boolean;
};

type LayoutState = {
  columnCount: number;
  columnGap: number;
  horizontalPadding: number;
  verticalPadding: number;
  columnWidth: number;
  estimatedItemHeight: number;
  isDesktop: boolean;
};

type VirtualGridItem = VirtualItem;

type VirtualizerLike = {
  getVirtualItems: () => VirtualGridItem[];
  measure: () => void;
  scrollToIndex: (index: number, options: { align: "start" }) => void;
};

type SharedVirtualGridProps = {
  cards: SearchResultItem[];
  totalItemCount: number;
  layout: LayoutState;
  noResults: boolean;
  loading: boolean;
  hasReachedEnd: boolean;
  cart: boolean;
  onClick: (id: number) => void;
  priceHasCurrency: boolean;
  disableFavorites: boolean;
  enableImageSearch: boolean;
  onLoadMore?: () => boolean | void | Promise<boolean | void>;
  onResumePositionChange?: (item: SearchResultItem) => void;
  gridRef: React.RefObject<HTMLDivElement | null>;
  lazyLoadContainerSelector?: string;
  measureVersion: number;
  scrollToItemId?: number | null;
  isSlideInOpen?: boolean;
};

const BREAKPOINT_GT_SMALL = 601;
const BREAKPOINT_MEDIUM = 850;
const BREAKPOINT_LARGE = 1251;
const DESKTOP_CARD_HEIGHT = 470;
const MOBILE_CARD_HEIGHT = 160;
const CLOSE_RESTORE_DELAY_MS = 520; // must be after the searchPage animation ends
const DEBUG_SCROLL_TARGET_HIGHLIGHT_MS = 0; // set to >0 to visually highlight the target item when restoring scroll position after slide-in close
const OVERSCAN_ROWS = 3;

const getSearchScrollTop = () => {
  const windowScrollTop = typeof window === "undefined" ? 0 : window.scrollY;
  const searchScrollTop =
    typeof document === "undefined" ? 0 : (document.getElementById("masonary")?.scrollTop ?? 0);

  return Math.max(windowScrollTop, searchScrollTop);
};

function getGridConfig(viewportWidth: number) {
  if (viewportWidth < BREAKPOINT_GT_SMALL) {
    return {
      minColWidth: 0,
      columnGap: 8,
      horizontalPadding: 8,
      verticalPadding: 8,
      estimatedItemHeight: MOBILE_CARD_HEIGHT,
    };
  }

  if (viewportWidth >= BREAKPOINT_LARGE) {
    return {
      minColWidth: 280,
      columnGap: 16,
      horizontalPadding: 16,
      verticalPadding: 16,
      estimatedItemHeight: DESKTOP_CARD_HEIGHT,
    };
  }

  if (viewportWidth >= BREAKPOINT_MEDIUM) {
    return {
      minColWidth: 340,
      columnGap: 12,
      horizontalPadding: 8,
      verticalPadding: 8,
      estimatedItemHeight: DESKTOP_CARD_HEIGHT,
    };
  }

  return {
    minColWidth: 280,
    columnGap: 8,
    horizontalPadding: 8,
    verticalPadding: 8,
    estimatedItemHeight: DESKTOP_CARD_HEIGHT,
  };
}

function resolveLayout(viewportWidth: number, containerWidth: number): LayoutState {
  const config = getGridConfig(viewportWidth);

  const columnCount =
    viewportWidth < BREAKPOINT_GT_SMALL
      ? 1
      : Math.max(
          1,
          Math.floor(
            (Math.max(0, containerWidth - config.horizontalPadding * 2) + config.columnGap) /
              (config.minColWidth + config.columnGap)
          )
        );

  const contentWidth = Math.max(
    0,
    containerWidth - config.horizontalPadding * 2 - config.columnGap * (columnCount - 1)
  );

  return {
    columnCount,
    columnGap: config.columnGap,
    horizontalPadding: config.horizontalPadding,
    verticalPadding: config.verticalPadding,
    columnWidth: contentWidth / columnCount,
    estimatedItemHeight: config.estimatedItemHeight,
    isDesktop: viewportWidth >= BREAKPOINT_LARGE,
  };
}

function getInitialLayout(): LayoutState {
  if (typeof window === "undefined") {
    return {
      columnCount: 1,
      columnGap: 8,
      horizontalPadding: 8,
      verticalPadding: 8,
      columnWidth: 280,
      estimatedItemHeight: MOBILE_CARD_HEIGHT,
      isDesktop: false,
    };
  }

  return resolveLayout(window.innerWidth, window.innerWidth);
}

function createItemIndexLookup(items: SearchResultItem[]): Map<number, number> {
  const lookup = new Map<number, number>();

  items.forEach((item, index) => {
    if (item.id > 0 && !lookup.has(item.id)) {
      lookup.set(item.id, index);
    }
  });

  return lookup;
}

function getVirtualItemKey(index: number, cards: SearchResultItem[]): string {
  if (index >= cards.length) return `ghost-${index - cards.length}`;
  const item = cards[index];
  return item.id > 0 ? `item-${item.id}` : `item-${index}`;
}

type CloseAnchor = {
  itemId: number;
  itemIndex: number;
  offsetFromViewportTop: number;
};

function findVisibleCloseAnchor({
  gridElement,
  cards,
  viewportTop,
  viewportBottom,
}: {
  gridElement: HTMLDivElement | null;
  cards: SearchResultItem[];
  viewportTop: number;
  viewportBottom: number;
}): CloseAnchor | null {
  if (!gridElement) return null;

  const renderedCells = gridElement.querySelectorAll<HTMLElement>("[data-index]");
  let fullyVisibleCandidate: {
    itemId: number;
    itemIndex: number;
    top: number;
    left: number;
  } | null = null;
  let crossingTopCandidate: {
    itemId: number;
    itemIndex: number;
    top: number;
    left: number;
  } | null = null;
  let belowTopCandidate: { itemId: number; itemIndex: number; top: number; left: number } | null =
    null;

  for (const cell of renderedCells) {
    const index = Number(cell.dataset.index);
    if (!Number.isInteger(index) || index < 0 || index >= cards.length) continue;

    const item = cards[index];
    if (!item || item.id <= 0) continue;

    const rect = cell.getBoundingClientRect();
    if (rect.bottom <= viewportTop || rect.top >= viewportBottom) continue;

    const candidate = {
      itemId: item.id,
      itemIndex: index,
      top: rect.top,
      left: rect.left,
    };
    const isFullyVisible = rect.top >= viewportTop && rect.bottom <= viewportBottom;
    const intersectsViewportTop = rect.top <= viewportTop && rect.bottom > viewportTop;

    if (isFullyVisible) {
      if (
        !fullyVisibleCandidate ||
        candidate.top < fullyVisibleCandidate.top ||
        (candidate.top === fullyVisibleCandidate.top && candidate.left < fullyVisibleCandidate.left)
      ) {
        fullyVisibleCandidate = candidate;
      }
      continue;
    }

    if (intersectsViewportTop) {
      if (
        !crossingTopCandidate ||
        candidate.top > crossingTopCandidate.top ||
        (candidate.top === crossingTopCandidate.top && candidate.left < crossingTopCandidate.left)
      ) {
        crossingTopCandidate = candidate;
      }
      continue;
    }

    if (
      !belowTopCandidate ||
      candidate.top < belowTopCandidate.top ||
      (candidate.top === belowTopCandidate.top && candidate.left < belowTopCandidate.left)
    ) {
      belowTopCandidate = candidate;
    }
  }

  const anchorCandidate = fullyVisibleCandidate ?? crossingTopCandidate ?? belowTopCandidate;
  if (!anchorCandidate) return null;

  const viewportHeight = Math.max(1, viewportBottom - viewportTop);
  const offsetFromViewportTop = Math.max(
    -viewportHeight,
    Math.min(anchorCandidate.top - viewportTop, viewportHeight)
  );

  return {
    itemId: anchorCandidate.itemId,
    itemIndex: anchorCandidate.itemIndex,
    offsetFromViewportTop,
  };
}

function restoreCloseAnchorOffset({
  targetIndex,
  offsetFromViewportTop,
  virtualizer,
  findCellElement,
  getViewportTop,
  adjustViewportBy,
}: {
  targetIndex: number;
  offsetFromViewportTop: number;
  virtualizer: VirtualizerLike;
  findCellElement: (index: number) => HTMLElement | null;
  getViewportTop: () => number;
  adjustViewportBy: (delta: number) => void;
}) {
  const cell = findCellElement(targetIndex);
  if (!cell) {
    virtualizer.scrollToIndex(targetIndex, { align: "start" });
    return;
  }

  const delta = cell.getBoundingClientRect().top - getViewportTop() - offsetFromViewportTop;
  if (Math.abs(delta) < 0.5) return;

  adjustViewportBy(delta);
}

function useScrollAnchorOnMeasure({
  measureVersion,
  virtualizer,
  itemIndexById,
  scrollToItemId,
  isSlideInOpen,
  isTargetVisible,
  captureCloseAnchor,
  restoreCloseAnchor,
  getScrollOffset,
  setDebugScrollTargetIndex,
}: {
  measureVersion: number;
  virtualizer: VirtualizerLike;
  itemIndexById: Map<number, number>;
  scrollToItemId?: number | null;
  isSlideInOpen?: boolean;
  isTargetVisible: (targetIndex: number) => boolean;
  captureCloseAnchor: () => CloseAnchor | null;
  restoreCloseAnchor: (targetIndex: number, offsetFromViewportTop: number) => void;
  getScrollOffset: () => number | null;
  setDebugScrollTargetIndex: (index: number) => void;
}) {
  const itemIndexByIdRef = useRef(itemIndexById);
  itemIndexByIdRef.current = itemIndexById;

  const scrollToItemIdRef = useRef(scrollToItemId);
  scrollToItemIdRef.current = scrollToItemId;

  const isSlideInOpenRef = useRef(isSlideInOpen);
  isSlideInOpenRef.current = isSlideInOpen;

  const restoreCloseAnchorRef = useRef(restoreCloseAnchor);
  restoreCloseAnchorRef.current = restoreCloseAnchor;
  const setDebugScrollTargetIndexRef = useRef(setDebugScrollTargetIndex);
  setDebugScrollTargetIndexRef.current = setDebugScrollTargetIndex;

  const previousIsSlideInOpenRef = useRef(!!isSlideInOpen);
  const closeAnchorRef = useRef<CloseAnchor | null>(null);
  const closeRestoreTimeoutRef = useRef<number | null>(null);
  const openScrollOffsetRef = useRef<number | null>(null);

  const clearCloseRestoreTimeout = useCallback(() => {
    if (closeRestoreTimeoutRef.current !== null) {
      window.clearTimeout(closeRestoreTimeoutRef.current);
      closeRestoreTimeoutRef.current = null;
    }
  }, []);

  useLayoutEffect(() => {
    const wasOpen = previousIsSlideInOpenRef.current;
    const isOpen = !!isSlideInOpen;

    if (!wasOpen && isOpen) {
      openScrollOffsetRef.current = getScrollOffset();
      closeAnchorRef.current = null;
      clearCloseRestoreTimeout();
    } else if (wasOpen && !isOpen) {
      const openOffset = openScrollOffsetRef.current;
      const closeOffset = getScrollOffset();
      const didScrollWhileOpen =
        openOffset !== null && closeOffset !== null && Math.abs(closeOffset - openOffset) > 1;

      if (!didScrollWhileOpen) {
        closeAnchorRef.current = null;
        openScrollOffsetRef.current = null;
        clearCloseRestoreTimeout();
        previousIsSlideInOpenRef.current = isOpen;
        return;
      }

      closeAnchorRef.current = captureCloseAnchor();
      clearCloseRestoreTimeout();
      const runRestore = () => {
        if (isSlideInOpenRef.current) return;

        const closeAnchor = closeAnchorRef.current;
        if (!closeAnchor) return;

        const closeTargetIndex =
          itemIndexByIdRef.current.get(closeAnchor.itemId) ?? closeAnchor.itemIndex;

        virtualizer.measure();
        requestAnimationFrame(() => {
          if (isSlideInOpenRef.current) return;
          setDebugScrollTargetIndexRef.current(closeTargetIndex);
          restoreCloseAnchorRef.current(closeTargetIndex, closeAnchor.offsetFromViewportTop);
          closeAnchorRef.current = null;
          openScrollOffsetRef.current = null;
        });
      };

      closeRestoreTimeoutRef.current = window.setTimeout(runRestore, CLOSE_RESTORE_DELAY_MS);
    } else if (isOpen) {
      closeAnchorRef.current = null;
      clearCloseRestoreTimeout();
    }

    previousIsSlideInOpenRef.current = isOpen;
  }, [isSlideInOpen, captureCloseAnchor, clearCloseRestoreTimeout, getScrollOffset, virtualizer]);

  useEffect(() => clearCloseRestoreTimeout, [clearCloseRestoreTimeout]);

  useEffect(() => {
    if (measureVersion === 0) return;

    virtualizer.measure();

    requestAnimationFrame(() => {
      if (isSlideInOpenRef.current) {
        const currentScrollToItemId = scrollToItemIdRef.current;
        if (!currentScrollToItemId) return;

        const targetIndex = itemIndexByIdRef.current.get(currentScrollToItemId);
        if (targetIndex === undefined) return;

        if (isTargetVisible(targetIndex)) return;
        setDebugScrollTargetIndexRef.current(targetIndex);
        virtualizer.scrollToIndex(targetIndex, { align: "start" });
      }
    });
  }, [measureVersion, virtualizer, isTargetVisible]);
}

function useLoadMoreTrigger({
  onLoadMore,
  onResumePositionChange,
  cards,
  noResults,
  loading,
  hasReachedEnd,
  itemCount,
  columnCount,
  virtualItems,
  getScrollOffset,
}: {
  onLoadMore?: () => boolean | void | Promise<boolean | void>;
  onResumePositionChange?: (item: SearchResultItem) => void;
  cards: SearchResultItem[];
  noResults: boolean;
  loading: boolean;
  hasReachedEnd: boolean;
  itemCount: number;
  columnCount: number;
  virtualItems: VirtualGridItem[];
  getScrollOffset: () => number;
}) {
  const lastTriggeredItemCountRef = useRef<number | null>(null);
  const pendingTriggerItemCountRef = useRef<number | null>(null);
  const lastReportedResumeItemIdRef = useRef<number | null>(null);
  const lastLoadScrollOffsetRef = useRef<number | null>(null);

  // Refs so the trigger effect can read current values without them being deps.
  // This prevents the effect from re-running (and cancelling in-flight calls)
  // every time loading/onLoadMore changes — which was the core stall bug.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const onResumePositionChangeRef = useRef(onResumePositionChange);
  onResumePositionChangeRef.current = onResumePositionChange;
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const noResultsRef = useRef(noResults);
  noResultsRef.current = noResults;
  const hasReachedEndRef = useRef(hasReachedEnd);
  hasReachedEndRef.current = hasReachedEnd;
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const getScrollOffsetRef = useRef(getScrollOffset);
  getScrollOffsetRef.current = getScrollOffset;

  useEffect(() => {
    if (itemCount === 0 || noResults) {
      lastTriggeredItemCountRef.current = null;
      pendingTriggerItemCountRef.current = null;
    }
  }, [itemCount, noResults]);

  useEffect(() => {
    if (!onLoadMoreRef.current || noResultsRef.current || loadingRef.current || itemCount === 0)
      return;
    const scrollOffset = getScrollOffsetRef.current();
    if (scrollOffset <= 0) return;
    if (virtualItems.length === 0) return;

    // Loading a page changes itemCount and virtualItems, which re-runs this
    // effect even when the user has not moved. Require fresh downward scroll
    // before claiming the next page boundary so one gesture loads one page.
    if (
      lastTriggeredItemCountRef.current !== null &&
      lastTriggeredItemCountRef.current !== itemCount &&
      lastLoadScrollOffsetRef.current !== null &&
      scrollOffset <= lastLoadScrollOffsetRef.current
    ) {
      return;
    }

    let maxVisibleRealIndex = -1;
    for (const virtualItem of virtualItems) {
      if (virtualItem.index < itemCount) {
        maxVisibleRealIndex = Math.max(maxVisibleRealIndex, virtualItem.index);
      }
    }

    if (maxVisibleRealIndex < 0) return;

    const resumeItem = cardsRef.current[maxVisibleRealIndex];
    if (
      onResumePositionChangeRef.current &&
      resumeItem &&
      lastReportedResumeItemIdRef.current !== resumeItem.id
    ) {
      lastReportedResumeItemIdRef.current = resumeItem.id;
      onResumePositionChangeRef.current(resumeItem);
    }

    const threshold = Math.max(columnCount * (OVERSCAN_ROWS + 1), 10);
    if (maxVisibleRealIndex >= itemCount - threshold) {
      if (lastTriggeredItemCountRef.current === itemCount) return;
      if (pendingTriggerItemCountRef.current === itemCount) return;

      // Claim the slot synchronously before awaiting — prevents any concurrent
      // effect re-run (from virtualItems changes during the async call) from
      // firing a duplicate request for the same page boundary.
      pendingTriggerItemCountRef.current = itemCount;

      void (async () => {
        const didStartLoading = (await onLoadMoreRef.current?.()) !== false;
        if (didStartLoading || hasReachedEndRef.current) {
          // Loaded (or permanently done) — don't re-trigger for this count.
          lastTriggeredItemCountRef.current = itemCount;
          lastLoadScrollOffsetRef.current = scrollOffset;
        }
        // On transient failure just release the slot. Retrying is owned by
        // the searchContext retry effect + the store's backoff gate — this
        // trigger only detects "user is near the bottom".
        pendingTriggerItemCountRef.current = null;
      })();
      // No cleanup function — pendingTriggerItemCountRef gates concurrent calls
      // without cancellation, which was the root cause of the stall.
    }
  }, [virtualItems, itemCount, columnCount]);
}

const VirtualGridItemCell = memo(function VirtualGridItemCell({
  virtualItem,
  cards,
  columnWidth,
  columnGap,
  horizontalPadding,
  verticalPadding,
  scrollMargin,
  cart,
  onClick,
  priceHasCurrency,
  disableFavorites,
  enableImageSearch,
  lazyLoadContainerSelector,
  debugScrollTargetIndex,
}: {
  virtualItem: VirtualGridItem;
  cards: SearchResultItem[];
  columnWidth: number;
  columnGap: number;
  horizontalPadding: number;
  verticalPadding: number;
  scrollMargin: number;
  cart: boolean;
  onClick: (id: number) => void;
  priceHasCurrency: boolean;
  disableFavorites: boolean;
  enableImageSearch: boolean;
  lazyLoadContainerSelector?: string;
  debugScrollTargetIndex: number | null;
}) {
  const top = virtualItem.start - scrollMargin + verticalPadding;
  const left = horizontalPadding + virtualItem.lane * (columnWidth + columnGap);
  const isGhost = virtualItem.index >= cards.length;

  return (
    <MantineBox
      data-index={virtualItem.index}
      data-scroll-target={debugScrollTargetIndex === virtualItem.index}
      className={styles.virtualItemCell}
      style={{
        width: columnWidth,
        left,
        transform: `translateY(${top}px)`,
      }}
    >
      {isGhost ? (
        <GhostCard index={virtualItem.index - cards.length} />
      ) : cart ? (
        <SelectionCard item={cards[virtualItem.index]} removeSelection={onClick} />
      ) : (
        <ItemCard
          item={cards[virtualItem.index]}
          priceHasCurrency={priceHasCurrency}
          disableFavorites={disableFavorites}
          enableImageSearch={enableImageSearch}
          containerSelector={lazyLoadContainerSelector}
        />
      )}
    </MantineBox>
  );
});

function DesktopVirtualGrid({
  cards,
  totalItemCount,
  layout,
  noResults,
  loading,
  hasReachedEnd,
  cart,
  onClick,
  priceHasCurrency,
  disableFavorites,
  enableImageSearch,
  scrollContainerRef,
  onLoadMore,
  onResumePositionChange,
  gridRef,
  lazyLoadContainerSelector,
  measureVersion,
  scrollToItemId,
  isSlideInOpen,
}: SharedVirtualGridProps & {
  scrollContainerRef: React.RefObject<HTMLElement | null>;
}) {
  const [debugScrollTargetIndex, setDebugScrollTargetIndex] = useState<number | null>(null);
  const clearDebugTargetTimeoutRef = useRef<number | null>(null);

  const showDebugScrollTarget = useCallback((index: number) => {
    setDebugScrollTargetIndex(index);

    if (clearDebugTargetTimeoutRef.current !== null) {
      window.clearTimeout(clearDebugTargetTimeoutRef.current);
    }

    clearDebugTargetTimeoutRef.current = window.setTimeout(() => {
      setDebugScrollTargetIndex(null);
      clearDebugTargetTimeoutRef.current = null;
    }, DEBUG_SCROLL_TARGET_HIGHLIGHT_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (clearDebugTargetTimeoutRef.current !== null) {
        window.clearTimeout(clearDebugTargetTimeoutRef.current);
      }
    };
  }, []);

  const getItemKey = useCallback((index: number) => getVirtualItemKey(index, cards), [cards]);

  const virtualizer = useVirtualizer({
    count: totalItemCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => layout.estimatedItemHeight,
    lanes: layout.columnCount,
    gap: layout.columnGap,
    overscan: layout.columnCount * OVERSCAN_ROWS,
    getItemKey,
  });

  const itemIndexById = useMemo(() => createItemIndexLookup(cards), [cards]);
  const isTargetVisible = useCallback(
    (targetIndex: number) => {
      const scrollEl = scrollContainerRef.current;
      if (!scrollEl) return false;

      const virtualItem = virtualizer.getVirtualItems().find((item) => item.index === targetIndex);
      if (!virtualItem) return false;

      const viewportTop = scrollEl.scrollTop;
      const viewportBottom = viewportTop + scrollEl.clientHeight;
      const itemTop = virtualItem.start;
      const itemBottom = itemTop + virtualItem.size;

      return itemBottom > viewportTop && itemTop < viewportBottom;
    },
    [scrollContainerRef, virtualizer]
  );
  const captureCloseAnchor = useCallback(() => {
    const scrollEl = scrollContainerRef.current;
    if (!scrollEl) return null;

    return findVisibleCloseAnchor({
      gridElement: gridRef.current,
      cards,
      viewportTop: scrollEl.getBoundingClientRect().top,
      viewportBottom: scrollEl.getBoundingClientRect().bottom,
    });
  }, [cards, gridRef, scrollContainerRef]);

  const getScrollOffset = useCallback(() => {
    const scrollEl = scrollContainerRef.current;
    return scrollEl ? scrollEl.scrollTop : null;
  }, [scrollContainerRef]);

  const restoreCloseAnchor = useCallback(
    (targetIndex: number, offsetFromViewportTop: number) => {
      const scrollEl = scrollContainerRef.current;
      if (!scrollEl) return;

      restoreCloseAnchorOffset({
        targetIndex,
        offsetFromViewportTop,
        virtualizer,
        findCellElement: (index) =>
          gridRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`) ?? null,
        getViewportTop: () => scrollEl.getBoundingClientRect().top,
        adjustViewportBy: (delta) => {
          scrollEl.scrollTop += delta;
        },
      });
    },
    [gridRef, scrollContainerRef, virtualizer]
  );

  useScrollAnchorOnMeasure({
    measureVersion,
    virtualizer,
    itemIndexById,
    scrollToItemId,
    isSlideInOpen,
    isTargetVisible,
    captureCloseAnchor,
    restoreCloseAnchor,
    getScrollOffset,
    setDebugScrollTargetIndex: showDebugScrollTarget,
  });

  const virtualItems = virtualizer.getVirtualItems();
  useLoadMoreTrigger({
    onLoadMore,
    onResumePositionChange,
    cards,
    noResults,
    loading,
    hasReachedEnd,
    itemCount: cards.length,
    columnCount: layout.columnCount,
    virtualItems,
    getScrollOffset: () => scrollContainerRef.current?.scrollTop ?? 0,
  });

  return (
    <MantineBox
      ref={gridRef}
      className={styles.virtualGridOuter}
      style={{
        height: virtualizer.getTotalSize() + layout.verticalPadding * 2,
        position: "relative",
        width: "100%",
      }}
    >
      {virtualItems.map((virtualItem) => (
        <VirtualGridItemCell
          key={virtualItem.key}
          virtualItem={virtualItem}
          cards={cards}
          columnWidth={layout.columnWidth}
          columnGap={layout.columnGap}
          horizontalPadding={layout.horizontalPadding}
          verticalPadding={layout.verticalPadding}
          scrollMargin={0}
          cart={cart}
          onClick={onClick}
          priceHasCurrency={priceHasCurrency}
          disableFavorites={disableFavorites}
          enableImageSearch={enableImageSearch}
          lazyLoadContainerSelector={lazyLoadContainerSelector}
          debugScrollTargetIndex={debugScrollTargetIndex}
        />
      ))}
    </MantineBox>
  );
}

function MobileVirtualGrid({
  cards,
  totalItemCount,
  layout,
  noResults,
  loading,
  hasReachedEnd,
  cart,
  onClick,
  priceHasCurrency,
  disableFavorites,
  enableImageSearch,
  onLoadMore,
  onResumePositionChange,
  gridRef,
  lazyLoadContainerSelector,
  measureVersion,
  scrollToItemId,
  isSlideInOpen,
}: SharedVirtualGridProps) {
  const [debugScrollTargetIndex, setDebugScrollTargetIndex] = useState<number | null>(null);
  const clearDebugTargetTimeoutRef = useRef<number | null>(null);

  const showDebugScrollTarget = useCallback((index: number) => {
    setDebugScrollTargetIndex(index);

    if (clearDebugTargetTimeoutRef.current !== null) {
      window.clearTimeout(clearDebugTargetTimeoutRef.current);
    }

    clearDebugTargetTimeoutRef.current = window.setTimeout(() => {
      setDebugScrollTargetIndex(null);
      clearDebugTargetTimeoutRef.current = null;
    }, DEBUG_SCROLL_TARGET_HIGHLIGHT_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (clearDebugTargetTimeoutRef.current !== null) {
        window.clearTimeout(clearDebugTargetTimeoutRef.current);
      }
    };
  }, []);

  const scrollMargin = gridRef.current?.offsetTop ?? 0;
  const getItemKey = useCallback((index: number) => getVirtualItemKey(index, cards), [cards]);

  const virtualizer = useWindowVirtualizer({
    count: totalItemCount,
    estimateSize: () => layout.estimatedItemHeight,
    lanes: layout.columnCount,
    gap: layout.columnGap,
    overscan: layout.columnCount * OVERSCAN_ROWS,
    scrollMargin,
    getItemKey,
  });

  const itemIndexById = useMemo(() => createItemIndexLookup(cards), [cards]);
  const isTargetVisible = useCallback(
    (targetIndex: number) => {
      if (typeof window === "undefined") return false;

      const virtualItem = virtualizer.getVirtualItems().find((item) => item.index === targetIndex);
      if (!virtualItem) return false;

      const scrollMargin = virtualizer.options.scrollMargin ?? gridRef.current?.offsetTop ?? 0;
      const viewportTop = window.scrollY;
      const viewportBottom = viewportTop + window.innerHeight;
      const itemTop = virtualItem.start - scrollMargin;
      const itemBottom = itemTop + virtualItem.size;

      return itemBottom > viewportTop && itemTop < viewportBottom;
    },
    [gridRef, virtualizer]
  );
  const captureCloseAnchor = useCallback(() => {
    if (typeof window === "undefined") return null;

    return findVisibleCloseAnchor({
      gridElement: gridRef.current,
      cards,
      viewportTop: 0,
      viewportBottom: window.innerHeight,
    });
  }, [cards, gridRef]);

  const getScrollOffset = useCallback(() => {
    if (typeof window === "undefined") return null;
    return window.scrollY;
  }, []);

  const restoreCloseAnchor = useCallback(
    (targetIndex: number, offsetFromViewportTop: number) => {
      if (typeof window === "undefined") return;

      restoreCloseAnchorOffset({
        targetIndex,
        offsetFromViewportTop,
        virtualizer,
        findCellElement: (index) =>
          gridRef.current?.querySelector<HTMLElement>(`[data-index="${index}"]`) ?? null,
        getViewportTop: () => 0,
        adjustViewportBy: (delta) => {
          window.scrollTo({ top: window.scrollY + delta });
        },
      });
    },
    [gridRef, virtualizer]
  );

  useScrollAnchorOnMeasure({
    measureVersion,
    virtualizer,
    itemIndexById,
    scrollToItemId,
    isSlideInOpen,
    isTargetVisible,
    captureCloseAnchor,
    restoreCloseAnchor,
    getScrollOffset,
    setDebugScrollTargetIndex: showDebugScrollTarget,
  });

  const virtualItems = virtualizer.getVirtualItems();
  useLoadMoreTrigger({
    onLoadMore,
    onResumePositionChange,
    cards,
    noResults,
    loading,
    hasReachedEnd,
    itemCount: cards.length,
    columnCount: layout.columnCount,
    virtualItems,
    getScrollOffset: getSearchScrollTop,
  });

  return (
    <MantineBox
      ref={gridRef}
      className={styles.virtualGridOuter}
      style={{
        height: virtualizer.getTotalSize() + layout.verticalPadding * 2,
        position: "relative",
        width: "100%",
      }}
    >
      {virtualItems.map((virtualItem) => (
        <VirtualGridItemCell
          key={virtualItem.key}
          virtualItem={virtualItem}
          cards={cards}
          columnWidth={layout.columnWidth}
          columnGap={layout.columnGap}
          horizontalPadding={layout.horizontalPadding}
          verticalPadding={layout.verticalPadding}
          scrollMargin={virtualizer.options.scrollMargin ?? 0}
          cart={cart}
          onClick={onClick}
          priceHasCurrency={priceHasCurrency}
          disableFavorites={disableFavorites}
          enableImageSearch={enableImageSearch}
          lazyLoadContainerSelector={lazyLoadContainerSelector}
          debugScrollTargetIndex={debugScrollTargetIndex}
        />
      ))}
    </MantineBox>
  );
}

const VirtualizedCardGrid = ({
  items,
  loading,
  ghosts = 20,
  cart,
  handleCardClick,
  noResults = false,
  showProAd = false,
  proAdVariant = "tokens",
  priceHasCurrency = false,
  disableFavorites = false,
  enableImageSearch = false,
  scrollContainerRef,
  onLoadMore,
  onResumePositionChange,
  lazyLoadContainerSelector = "#masonary",
  scrollToItemId,
  isSlideInOpen,
}: Props) => {
  const onClick = handleCardClick || (() => {});
  const hasReachedEnd = useItemsStore((s) => s.hasReachedEnd);
  // itemsStore drops `loading` back to false during the backoff window between
  // failed load-more attempts (so the retry-scheduling effect in searchContext
  // can re-arm), which would otherwise make the spinner/ghosts disappear and
  // reappear on every retry. shouldRetry stays true for the whole window, so
  // the loading UI stays put until a retry actually succeeds or gives up.
  const shouldRetry = useItemsStore((s) => s.shouldRetry);
  const gridRef = useRef<HTMLDivElement>(null);
  const cardGridRef = useRef<HTMLDivElement>(null);
  const suspendLayoutRef = useRef(false);
  const resumeLayoutTimeoutRef = useRef<number | null>(null);

  const [layout, setLayout] = useState<LayoutState>(getInitialLayout);
  const [measureVersion, setMeasureVersion] = useState(0);
  const lastLayoutInputRef = useRef<{ viewportWidth: number; containerWidth: number } | null>(null);

  const cards = useMemo(() => {
    if (!showProAd) return items;

    const adCopy =
      proAdVariant === "signup"
        ? {
            title: "Sign up for free",
            description: `
            <h4>Create a free KURB account to unlock more.</h4>
            <ul>
              <li>Save unlimited searches with instant alerts</li>
              <li>Track price drops on your favorites</li>
              <li>A daily token budget for Kurbo, our real-time search mode</li>
            </ul>
            `,
            // Both url and itemurl use the same sign-in sentinel — ItemCard
            // checks for it before the normal url!==itemurl navigation
            // logic, since there's no dedicated sign-up page to link to.
            url: AD_ACTION_SIGN_IN,
            itemurl: AD_ACTION_SIGN_IN,
            vendorLabel: "Sign up",
          }
        : {
            title: "Get more tokens",
            description: `
            <h4>More tokens, faster alerts. Try Pro free for 14 days.</h4>
            <ul>
              <li>Larger daily token budget</li>
              <li>Instant, 15-minute, and 30-minute notification intervals</li>
              <li>Email summary of new listings</li>
              <li>Private collections</li>
            </ul>
            `,
            url: "/plans",
            itemurl: "/plans",
            vendorLabel: "See plans",
          };

    const withAds = items.reduce((acc: SearchResultItem[], item, index) => {
      acc.push(item);
      if ((index + 1) % 50 === 0) {
        const proImageIndex = (Math.floor(index / 5) % 3) + 1;
        acc.push({
          id: 0,
          title: adCopy.title,
          description: adCopy.description,
          imageurl: `/pro-${proImageIndex}.jpg`,
          url: adCopy.url,
          itemurl: adCopy.itemurl,
          region: "Global",
          price: "",
          currency: "",
          vendorname: "KURB",
          vendor: { label: adCopy.vendorLabel, id: 0 },
          brand: { label: "KURB", id: 0 },
        } as unknown as SearchResultItem);
      }
      return acc;
    }, []);
    return withAds;
  }, [items, showProAd, proAdVariant]);

  const updateLayout = useCallback(
    (force = false) => {
      if (!force && suspendLayoutRef.current) return;

      const viewportWidth = window.innerWidth;
      const containerWidth =
        cardGridRef.current?.clientWidth ??
        gridRef.current?.clientWidth ??
        scrollContainerRef?.current?.clientWidth ??
        viewportWidth;

      if (containerWidth <= 0) return;

      const lastInput = lastLayoutInputRef.current;
      if (
        lastInput &&
        lastInput.viewportWidth === viewportWidth &&
        lastInput.containerWidth === containerWidth
      ) {
        return;
      }
      lastLayoutInputRef.current = { viewportWidth, containerWidth };

      const next = resolveLayout(viewportWidth, containerWidth);
      setLayout((prev) => {
        if (prev.columnCount !== next.columnCount) {
          setMeasureVersion((v) => v + 1);
        }

        if (
          prev.columnCount === next.columnCount &&
          prev.columnGap === next.columnGap &&
          prev.horizontalPadding === next.horizontalPadding &&
          prev.verticalPadding === next.verticalPadding &&
          prev.columnWidth === next.columnWidth &&
          prev.estimatedItemHeight === next.estimatedItemHeight &&
          prev.isDesktop === next.isDesktop
        ) {
          return prev;
        }

        return next;
      });
    },
    [scrollContainerRef]
  );

  useEffect(() => {
    updateLayout(true);
    const onResize = () => updateLayout();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [updateLayout]);

  useEffect(() => {
    const ro = new ResizeObserver(() => updateLayout());

    const cardGrid = cardGridRef.current;
    if (cardGrid) ro.observe(cardGrid);

    const scrollEl = scrollContainerRef?.current;
    if (scrollEl) ro.observe(scrollEl);

    return () => {
      ro.disconnect();
    };
  }, [scrollContainerRef, updateLayout]);

  useEffect(() => {
    suspendLayoutRef.current = true;
    if (resumeLayoutTimeoutRef.current !== null) {
      window.clearTimeout(resumeLayoutTimeoutRef.current);
    }

    resumeLayoutTimeoutRef.current = window.setTimeout(() => {
      suspendLayoutRef.current = false;
      updateLayout(true);
      resumeLayoutTimeoutRef.current = null;
    }, CLOSE_RESTORE_DELAY_MS);

    return () => {
      if (resumeLayoutTimeoutRef.current !== null) {
        window.clearTimeout(resumeLayoutTimeoutRef.current);
        resumeLayoutTimeoutRef.current = null;
      }
      suspendLayoutRef.current = false;
    };
  }, [isSlideInOpen, updateLayout]);

  // Add temporary ghost slots during pagination so the virtualized grid
  // can render loading placeholders at the end of the current results.
  const totalItemCount = cards.length + (loading || shouldRetry ? ghosts : 0);

  if (noResults) {
    return (
      <MantineBox className={styles.cardGrid} data-noresults={true}>
        <MantineBox className={styles.gridContainer}>
          <MantineTitle order={2}>NO RESULTS FOUND</MantineTitle>
        </MantineBox>
      </MantineBox>
    );
  }

  if (!items.length) {
    return (
      <MantineBox className={styles.cardGrid} data-noresults={false} ref={cardGridRef}>
        <MantineBox className={styles.gridContainer} id="masonaryContainer" ref={gridRef}>
          {Array.from({ length: ghosts }).map((_, i) => (
            <GhostCard key={`ghost-${i}`} index={i} />
          ))}
        </MantineBox>
      </MantineBox>
    );
  }

  const sharedProps: SharedVirtualGridProps = {
    cards,
    totalItemCount,
    layout,
    noResults,
    loading,
    hasReachedEnd,
    cart: !!cart,
    onClick,
    priceHasCurrency,
    disableFavorites,
    enableImageSearch,
    onLoadMore,
    onResumePositionChange,
    gridRef,
    lazyLoadContainerSelector,
    measureVersion,
    scrollToItemId,
    isSlideInOpen,
  };

  const showLoadMoreSpinner = items.length > 0 && (loading || shouldRetry) && !hasReachedEnd;

  return (
    <MantineBox
      className={styles.cardGrid}
      data-noresults={false}
      id="masonaryContainer"
      ref={cardGridRef}
    >
      {layout.isDesktop && scrollContainerRef ? (
        <DesktopVirtualGrid {...sharedProps} scrollContainerRef={scrollContainerRef} />
      ) : (
        <MobileVirtualGrid {...sharedProps} />
      )}
      {showLoadMoreSpinner && (
        <MantineBox className={styles.loadMoreSpinner} data-testid="load-more-spinner">
          <MantineText span className={styles.loadMoreSpinnerBadge}>
            <MantineLoader size="sm" type="oval" />
          </MantineText>
        </MantineBox>
      )}
    </MantineBox>
  );
};

export default VirtualizedCardGrid;
